import classNames from 'classnames';
import { FrequencyDropdown } from 'components/missed-deal-feedback/ready/table/frequency-dropdown';
import { Percent } from 'components/shared/Percent/Percent';
import type { CompetitionParameterFeedback } from 'components/shared/WithErrorTooltip/model';
import { IntlKey } from 'locales';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CouponDefType, MissedFeedbackCompetitionParameter } from 'state/missed-deal-feeedback/feedback/model';
import { snakeCaseToWords } from 'utils/string';

interface Props {
  selectedParameter?: MissedFeedbackCompetitionParameter;
  availableParameters: readonly (IntlKey | string)[];
  bestOfferValue?: number;
  bestOfferFrequency?: CouponDefType;
  onChange(feedback: CompetitionParameterFeedback): void;
  onRemove(): void;
}
export function CompetitionParameterTableRow({
  selectedParameter,
  availableParameters,
  bestOfferValue,
  bestOfferFrequency,
  onChange,
  onRemove,
}: Props) {
  const { formatNumber } = useIntl();

  const buildUnit = useCallback(
    (frequency: any) => {
      if (selectedParameter?.unit === undefined) {
        return undefined;
      }

      return `${selectedParameter?.unit} ${frequency ?? ''}`.trim();
    },
    [selectedParameter?.unit],
  );

  const hasValueChanged = useMemo(
    () => bestOfferValue !== undefined && bestOfferValue !== selectedParameter?.value,
    [bestOfferValue, selectedParameter?.value],
  );

  const hasFrequencyChanged = useMemo(
    () => bestOfferFrequency !== undefined && bestOfferFrequency !== selectedParameter?.couponDefType,
    [bestOfferFrequency, selectedParameter?.couponDefType],
  );

  return (
    <tr data-e2e="competition-parameters-table-row">
      <td className="text-nowrap border" scope="row">
        <select
          className="pe-0 form-select"
          data-e2e="competition-parameters-select"
          value={selectedParameter?.label ?? ''}
          onChange={event => onChange({ label: event.target.value, bestOfferValue, bestOfferFrequency })}
        >
          {!selectedParameter && (
            <option value="">
              <FormattedMessage id="missedDealFeedback.selectParameter" />
            </option>
          )}
          {selectedParameter && (
            <option value={selectedParameter.label}>
              <FormattedMessage
                id={selectedParameter.label as IntlKey}
                defaultMessage={snakeCaseToWords(selectedParameter.label ?? '')}
              />
            </option>
          )}
          {availableParameters.map(parameter => (
            <option value={parameter} key={parameter}>
              <FormattedMessage id={parameter as IntlKey} defaultMessage={snakeCaseToWords(parameter)} />
            </option>
          ))}
        </select>
      </td>
      <td>
        {selectedParameter?.value !== undefined && (
          <div className="input-group">
            <input
              readOnly
              data-e2e="competition-parameters-sg-input"
              className="form-control text-muted"
              type="text"
              value={formatNumber(selectedParameter.value, {
                maximumFractionDigits: 4,
              })}
            />
            {selectedParameter && (
              <span className="unit input-group-text">{buildUnit(selectedParameter.couponDefType)}</span>
            )}
          </div>
        )}
      </td>
      <td className="border">
        {selectedParameter !== undefined && (
          <div className="input-group">
            <Percent
              e2e="competition-parameters-best-offer-input"
              className={classNames({
                'is-warning': bestOfferValue === undefined,
                'border-bottom border-info text-info': hasValueChanged,
              })}
              onChange={newValue =>
                onChange({
                  label: selectedParameter?.label,
                  bestOfferFrequency: bestOfferFrequency ?? selectedParameter.couponDefType,
                  bestOfferValue: newValue,
                })
              }
              value={bestOfferValue}
              precision={4}
            />
            <span
              className={classNames('unit input-group-text', {
                'text-info': hasValueChanged,
              })}
              data-e2e="unit"
            >
              {selectedParameter.unit ?? ''}
            </span>
            {selectedParameter.couponDefType && (
              <div className="input-group-btn">
                <FrequencyDropdown
                  selectedFrequency={bestOfferFrequency ?? selectedParameter.couponDefType}
                  hasValueChanged={hasFrequencyChanged}
                  className="h-100"
                  onFrequencySelected={newFrequency =>
                    onChange({
                      label: selectedParameter?.label,
                      bestOfferFrequency: newFrequency,
                      bestOfferValue,
                    })
                  }
                />
              </div>
            )}
          </div>
        )}
      </td>
      <td className="align-middle text-center px-1">
        <button className="btn btn-icon" type="button" onClick={onRemove}>
          <i className="clickable icon text-secondary" data-e2e="selected-underlying-item-delete">
            cancel
          </i>
        </button>
      </td>
    </tr>
  );
}
